import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { PrivacyNoticeSheet } from '@/consent/PrivacyNoticeSummary';
import { currentEnvironment, ENVIRONMENT_LABEL_KEY } from '../environment';
import { GUIDE_PATH, SERVICE_STATUS_PATH, SUPPORT_EMAIL, SUPPORT_HREF } from './service-links';

/**
 * The frame's footer (decision 49, Brand Kit 1.0.0 § The frame): who publishes the product, which
 * version is running, in which environment, and the service links.
 *
 * FOUR SERVICE LINKS, EACH WITH A DESTINATION: Guida, Privacy, Stato del servizio, Supporto, in
 * the proposal's order. A link to nowhere is an inert control, which the standard forbids, so each
 * one leads somewhere real even before its content exists: Guida and Stato del servizio to their
 * placeholder pages, Supporto to an email, Privacy to the notice's own sheet. The destinations live
 * in `service-links.ts`.
 *
 * THE VERSION IS THE BUILD'S OWN. `__APP_VERSION__` is `package.json`'s version, injected by Vite at
 * build time, so the footer cannot drift from what was released. The environment is the build mode
 * (`environment.ts`).
 *
 * Two hosts, one component: at the end of the page from 620px up, and at the foot of the navigation
 * drawer, which is where a phone keeps it — the page footer is `display: none` there (`shell.css`).
 * In the drawer, following a link closes the drawer, as every other entry in it does.
 */
export function ShellFooter({
  variant = 'page',
  onNavigate,
}: {
  readonly variant?: 'page' | 'drawer';
  /** Called after a page link is followed — the drawer closes on it. */
  readonly onNavigate?: () => void;
}) {
  const { t } = useTranslation('shell');
  const environment = currentEnvironment();

  return (
    <footer className="shell-footer" data-variant={variant}>
      <p className="shell-footer-meta">
        <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
        <span aria-hidden="true">·</span>
        <span>
          {t('appName')}{' '}
          <span
            className="shell-footer-version"
            title={t('footer.version', { version: __APP_VERSION__ })}
          >
            {__APP_VERSION__}
          </span>
        </span>
        <span aria-hidden="true">·</span>
        <span className="shell-footer-env" data-env={environment}>
          {t(ENVIRONMENT_LABEL_KEY[environment])}
        </span>
      </p>
      <nav className="shell-footer-links" aria-label={t('footer.linksLabel')}>
        <Link className="shell-footer-link" to={GUIDE_PATH} onClick={onNavigate}>
          {t('nav.guide')}
        </Link>
        <PrivacyNoticeSheet
          trigger={
            <button type="button" className="shell-footer-link">
              {t('footer.privacy')}
            </button>
          }
        />
        <Link className="shell-footer-link" to={SERVICE_STATUS_PATH} onClick={onNavigate}>
          {t('nav.serviceStatus')}
        </Link>
        <a
          className="shell-footer-link"
          href={SUPPORT_HREF}
          title={t('footer.supportTitle', { email: SUPPORT_EMAIL })}
        >
          {t('footer.support')}
        </a>
      </nav>
    </footer>
  );
}
